import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { environment } from '../../environments/environment';

export interface PurchaseOrderItem {
  po_item_id?: number;
  pipe_id: number;
  pipe_tubo_type: string;
  quantity_ordered: number;
  unit_price: number;
  total_price: number;
  quantity_received: number;
  remaining_quantity: number;
  received_date?: string;
  notes?: string;
}

export interface Supplier {
  supplier_id: number;
  supplier_name: string;
  contact_person: string;
  phone: string;
  email: string;
}

export interface PurchaseOrderDialogData {
  pipes?: any[];
  po_id?: number;
}

@Component({
  selector: 'app-purchase-order-dialog',
  templateUrl: './purchase-order-dialog.component.html',
  styleUrls: ['./purchase-order-dialog.component.scss']
})
export class PurchaseOrderDialogComponent implements OnInit {
  orderForm: FormGroup;
  suppliers: Supplier[] = [];
  availablePipes: any[] = [];
  orderItems: PurchaseOrderItem[] = [];
  orderItemsDataSource = new MatTableDataSource<PurchaseOrderItem>();
  dataSource = new MatTableDataSource<PurchaseOrderItem>();
  displayedColumns: string[] = [
    'pipeTuboType', 'quantity', 'unitPrice', 'total', 'actions'
  ];
  loading = false;
  isEditMode = false;
  itemColumns: string[] = ['pipeType', 'quantity', 'unitPrice', 'total', 'actions'];

  constructor(
    private dialogRef: MatDialogRef<PurchaseOrderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PurchaseOrderDialogData,
    private fb: FormBuilder,
    private http: HttpClient
  ) {
    this.availablePipes = data.pipes || [];
    this.isEditMode = !!data.po_id;
    this.initForm();
  }

  ngOnInit(): void {
    this.loadSuppliers();
  }

  initForm(): void {
    this.orderForm = this.fb.group({
      poNumber: ['PO-' + Date.now(), Validators.required],
      supplierId: ['', Validators.required],
      orderDate: [new Date().toISOString().split('T')[0], Validators.required],
      expectedDeliveryDate: [''],
      actualDeliveryDate: [''],
      status: ['pending', Validators.required],
      notes: ['']
    });
  }

  loadSuppliers(): void {
    this.http.get<Supplier[]>(`${environment.apiServiceBaseUrl}/hawdco/suppliers`).subscribe({
      next: (data) => {
        this.suppliers = data;
      },
      error: (error) => {
        console.error('Error loading suppliers:', error);
      }
    });
  }

  addOrderItem(): void {
    const newItem: PurchaseOrderItem = {
      pipe_id: 0,
      pipe_tubo_type: '',
      quantity_ordered: 1,
      unit_price: 0,
      total_price: 0,
      quantity_received: 0,
      remaining_quantity: 1
    };
    this.orderItems.push(newItem);
    this.dataSource.data = this.orderItems;
  }

  removeOrderItem(index: number): void {
    this.orderItems.splice(index, 1);
    this.dataSource.data = this.orderItems;
    this.calculateTotals();
  }

  updateOrderItem(index: number, field: string, value: any): void {
    const item = this.orderItems[index];
    
    if (field === 'pipe_id') {
      const selectedPipe = this.availablePipes.find(p => p.pipe_id === value);
      if (selectedPipe) {
        item.pipe_tubo_type = selectedPipe.pipe_tubo_type;
        item.unit_price = selectedPipe.unit_cost || 0;
      }
    }
    
    if (field === 'quantity_ordered' || field === 'unit_price') {
      item.total_price = item.quantity_ordered * item.unit_price;
      item.remaining_quantity = item.quantity_ordered - item.quantity_received;
    }
    
    this.orderItems[index] = { ...item };
    this.dataSource.data = this.orderItems;
    this.calculateTotals();
  }

  calculateTotals(): number {
    // This can be used to display order totals in the UI
    const totalAmount = this.orderItems.reduce((sum, item) => sum + item.total_price, 0);
    return totalAmount;
  }

  onSave(): void {
    if (this.orderForm.invalid || this.orderItems.length === 0) {
      this.orderForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const formData = this.orderForm.value;
    formData.items = this.orderItems;
    formData.total_amount = this.calculateTotals();

    const endpoint = this.isEditMode 
      ? `${environment.apiServiceBaseUrl}/hawdco/purchase-orders/${this.data.po_id}`
      : `${environment.apiServiceBaseUrl}/hawdco/purchase-orders`;

    const request = this.isEditMode 
      ? this.http.put(endpoint, formData)
      : this.http.post(endpoint, formData);

    request.subscribe({
      next: () => {
        this.loading = false;
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error saving purchase order:', error);
        this.loading = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  getTotalAmount(): number {
    return this.orderItems.reduce((sum, item) => sum + item.total_price, 0);
  }

  getTotalQuantity(): number {
    return this.orderItems.reduce((sum, item) => sum + item.quantity_ordered, 0);
  }

  getSelectedPipes(): number[] {
    return this.orderItems.map(item => item.pipe_id).filter(id => id > 0);
  }

  getAvailablePipesForItem(currentItemId?: number): any[] {
    const selectedIds = this.getSelectedPipes();
    if (currentItemId) {
      const currentItem = this.orderItems.find(item => item.pipe_id === currentItemId);
      selectedIds.splice(selectedIds.indexOf(currentItemId), 1);
    }
    return this.availablePipes.filter(pipe => !selectedIds.includes(pipe.pipe_id));
  }

  onItemChange(index: number): void {
    const item = this.orderItems[index];
    // This method will be called when an item selection changes
    this.updateOrderItem(index, 'pipe_id', item.pipe_id);
  }

  calculateItemTotal(index: number): void {
    const item = this.orderItems[index];
    item.total_price = item.quantity_ordered * item.unit_price;
    item.remaining_quantity = item.quantity_ordered - item.quantity_received;
    this.orderItems[index] = { ...item };
    this.orderItemsDataSource.data = this.orderItems;
    this.calculateTotals();
  }
}
